import { gql } from "@apollo/client";
import type { SyncableFacility } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { Facility as RxFacility } from "../collections/facility";
import type { RxDBCollections } from "../provider";
import {
  SyncFacilityPullDocument,
  type WholeSyncableFacilityFragment,
} from "./__generated__/facility.gql";
import { useGqlReplication } from "./use-gql-replication";

export const useReplicateFacility = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxFacility>;
}) => {
  const pull = useMemo(
    () => ({
      query: SyncFacilityPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { hours: 1 },
    }),
    [],
  );

  return useGqlReplication<RxFacility, SyncableFacility, null>({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlFacilityToRxFacility,
    batchSize: 1000,
    metadataLocalStorageKeySuffix: "facility",
  });
};

export const mapGqlFacilityToRxFacility = (
  facility: WholeSyncableFacilityFragment,
): WithDeleted<RxFacility> => ({
  id: facility.id,
  name: facility.name,
  addresses: facility.addresses.map((address) => ({
    country: address.country ?? null,
    state: address.state ?? null,
    street: address.street ?? null,
    subCounty: address.subCounty ?? null,
    ward: address.ward ?? null,
  })),
  organisation: facility.organisation,
  service: facility.service ?? null,
  settings: {
    inventoryManagement: facility.settings.inventoryManagement,
  },
  meta: {
    createdAt: facility.meta.createdAt,
    createdByUserId: facility.meta.createdByUserId,
    updatedAt: facility.meta.updatedAt,
    updatedByUserId: facility.meta.updatedByUserId,
  },
  _deleted: facility.meta.deleted,
});

gql`
  fragment WholeSyncableFacility on SyncableFacility {
    id
    name
    addresses {
      country
      state
      street
      subCounty
      ward
    }
    organisation
    service
    settings {
      inventoryManagement
    }
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncFacilityPull($input: PullFacilityInput!) {
    pullFacility(input: $input) {
      documents {
        ...WholeSyncableFacility
      }
    }
  }
`;
